import { Button, Group, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import type React from "react";
import type { JSX } from "react";
import { z } from "zod/v4";

const itemSchema = z
    .object({
        type: z.enum(["MESSAGE", "FUNCTION_CALL", "FUNCTION_CALL_OUTPUT", "REASONING"]),
        role: z.enum(["USER", "ASSISTANT"]).nullable(),
        content: z.string(),
        tool_name: z.string().nullable().optional(),
        tool_arguments: z.string().nullable().optional(),
        tool_call_id: z.string().nullable().optional(),
        reasoning_id: z.string().nullable().optional(),
        reasoning_summary: z.array(z.string()).optional(),
        encrypted_reasoning: z.string().nullable().optional(),
        rating: z.union([z.literal("up"), z.literal("down"), z.null()]).optional(),
    })
    .superRefine((val, ctx) => {
        // role must be present (non-null) only when type === "MESSAGE"; otherwise it must be null
        if (val.type === "MESSAGE") {
            if (val.role === null) {
                ctx.addIssue({
                    code: "custom",
                    path: ["role"],
                    message: "role is required when type is MESSAGE",
                });
            }
        } else {
            if (val.role !== null) {
                ctx.addIssue({
                    code: "custom",
                    path: ["role"],
                    message: "role must be null when type is not MESSAGE",
                });
            }
        }
    });

const threadSchema = z.object({
    personality: z.uuid(),
    personality_name: z.string().nullable().optional(),
    ai_thread_items: z.array(itemSchema),
});

const inputArraySchema = z.array(threadSchema);
const inputUnionSchema = z.union([inputArraySchema, threadSchema]);

export type IParsedInput = {
    threads: z.infer<typeof threadSchema>[];
    _inputKind: "array" | "single";
};

interface IFormValues {
    content: string;
}

interface IProps {
    handleNext: (values: IParsedInput) => void;
}

function parseContent(jsonString: string): IParsedInput {
    const raw = JSON.parse(jsonString);
    const parsed = inputUnionSchema.parse(raw);
    if (Array.isArray(parsed)) {
        return { threads: parsed, _inputKind: "array" };
    }
    return { threads: [parsed], _inputKind: "single" };
}

function validateContent(jsonString: string): string | null {
    try {
        parseContent(jsonString);
    } catch (error) {
        if (error instanceof z.ZodError) {
            console.error("Validation errors:", error.issues);
            return `Validation errors: ${JSON.stringify(error.issues)}`;
        }
        console.error("Failed to parse JSON:", error);
        return `Failed to parse JSON: ${String(error)}`;
    }
    return null;
}

function ImportInputForm({ handleNext }: IProps): JSX.Element {
    const form = useForm<IFormValues>({
        initialValues: {
            content: "",
        },
        validate: {
            content: (value) => validateContent(value),
        },
    });

    const handleSubmit = (values: IFormValues) => {
        handleNext(parseContent(values.content));
    };

    const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
        // Allow dropping files into the textarea
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (!file) {
            return;
        }

        const isJson =
            file.name.toLowerCase().endsWith(".json") || file.type === "application/json" || file.type === "text/json";
        if (!isJson) {
            notifications.show({ title: "Unsupported file", message: "Please drop a .json file", color: "red" });
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result ?? "");
            form.setFieldValue("content", text);
            // Trigger validation right away so user sees any issues
            form.validateField("content");
        };
        reader.onerror = () => {
            notifications.show({ title: "Failed to read file", message: "Could not read the file.", color: "red" });
        };
        reader.readAsText(file);
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Textarea
                withAsterisk
                label="JSON"
                description="You can also drag & drop a .json file into this area"
                autosize
                minRows={8}
                maxRows={16}
                {...form.getInputProps("content")}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            />
            <Group justify="flex-end" mt="md">
                <Button type="submit">Next</Button>
            </Group>
        </form>
    );
}

export default ImportInputForm;
